import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, CreditCard, Check, Loader2, BadgeCheck, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small shared header so the cards drop cleanly into a BentoTile.
const CardHead = ({ icon: Icon, eyebrow, title, verified }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="cn-eyebrow">{eyebrow}</p>
        <h3 className="cn-display text-lg text-cn-text dark:text-white leading-tight">{title}</h3>
      </div>
    </div>
    {verified && (
      <Badge className="bg-cn-success/10 text-cn-success border border-cn-success/25 whitespace-nowrap" data-testid="insurance-verified-badge">
        <BadgeCheck className="w-3 h-3 mr-1" /> Verified
      </Badge>
    )}
  </div>
);

const usePayers = () => {
  const [payers, setPayers] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  useEffect(() => {
    axios.get(`${API}/insurance/payers`)
      .then((r) => { setPayers(r.data.payers || []); setPlanTypes(r.data.plan_types || []); })
      .catch(() => {});
  }, []);
  return { payers, planTypes };
};

/* ---------------- Clinician: declare accepted insurance ---------------- */
export const ClinicianInsuranceCard = ({ getAuthHeader }) => {
  const { payers, planTypes } = usePayers();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accepts, setAccepts] = useState(false);
  const [verified, setVerified] = useState(false);
  const [selPayers, setSelPayers] = useState([]);
  const [selPlans, setSelPlans] = useState([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    axios.get(`${API}/clinician/profile`, getAuthHeader())
      .then((r) => {
        const p = r.data || {};
        setAccepts(!!p.accepts_insurance);
        setVerified(!!p.insurance_verified);
        setSelPayers(p.accepted_payers || []);
        setSelPlans(p.accepted_plan_types || []);
        setNotes(p.insurance_notes || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePayer = (id) =>
    setSelPayers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const togglePlan = (id) =>
    setSelPlans((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/clinician/insurance`, {
        accepts_insurance: accepts,
        accepted_payers: selPayers,
        accepted_plan_types: selPlans,
        insurance_notes: notes || null,
      }, getAuthHeader());
      setVerified(!!data.insurance_verified);
      setSelPayers(data.accepted_payers || []);
      setSelPlans(data.accepted_plan_types || []);
      toast.success('Insurance preferences saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save insurance preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-cn-primary animate-spin" /></div>;
  }

  return (
    <div data-testid="clinician-insurance-card">
      <CardHead icon={ShieldCheck} eyebrow="Payments" title="Insurance you accept" verified={verified && accepts} />

      <label className="mt-5 flex items-center justify-between gap-4 p-4 rounded-2xl bg-cn-bg/60 border border-cn-border/60 cursor-pointer">
        <div>
          <p className="font-semibold text-cn-text dark:text-white text-sm">I accept insurance</p>
          <p className="text-xs text-cn-text-muted mt-0.5">Show an "in-network" badge to matching families.</p>
        </div>
        <Switch checked={accepts} onCheckedChange={setAccepts} data-testid="clinician-accepts-insurance-switch" />
      </label>

      {accepts && (
        <div className="mt-5 space-y-5 animate-fade-in-up">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Accepted payers</p>
            <div className="flex flex-wrap gap-2">
              {payers.map((p) => {
                const on = selPayers.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePayer(p.id)}
                    aria-pressed={on}
                    data-testid={`clinician-payer-${p.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      on ? 'bg-cn-primary text-white border-cn-primary shadow-sm'
                         : 'bg-white dark:bg-white/5 text-cn-text border-cn-border hover:border-cn-primary/40'
                    }`}
                  >
                    {on && <Check className="w-3 h-3 mr-1 inline" />}{p.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Plan types</p>
            <div className="flex flex-wrap gap-2">
              {planTypes.map((p) => {
                const on = selPlans.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePlan(p.id)}
                    aria-pressed={on}
                    data-testid={`clinician-plan-${p.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      on ? 'bg-cn-accent text-white border-cn-accent shadow-sm'
                         : 'bg-white dark:bg-white/5 text-cn-text border-cn-border hover:border-cn-accent/40'
                    }`}
                  >
                    {on && <Check className="w-3 h-3 mr-1 inline" />}{p.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Notes (optional)</p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. I bill most PPO plans directly; copays vary by plan."
              className="min-h-[72px] rounded-2xl border-cn-border bg-white dark:bg-white/5"
              data-testid="clinician-insurance-notes"
            />
          </div>

          <p className="text-[11px] text-cn-text-muted leading-relaxed">
            Self-attested. CaraLoom does not verify benefits — families should confirm coverage with their plan before booking.
            Editing this resets admin verification.
          </p>
        </div>
      )}

      <Button onClick={save} disabled={saving} className="cn-btn cn-btn-primary w-full mt-5 !py-2.5" data-testid="clinician-save-insurance">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {saving ? 'Saving…' : 'Save insurance preferences'}
      </Button>
    </div>
  );
};

/* ---------------- Patient: record their insurance ---------------- */
export const PatientInsuranceCard = ({ getAuthHeader }) => {
  const { payers } = usePayers();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payerId, setPayerId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [savedProvider, setSavedProvider] = useState(null);

  useEffect(() => {
    axios.get(`${API}/patient/profile`, getAuthHeader())
      .then((r) => {
        const p = r.data || {};
        setPayerId(p.insurance_payer_id || '');
        setMemberId(p.insurance_id || '');
        setSavedProvider(p.insurance_provider || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/patient/insurance`, {
        insurance_payer_id: payerId || null,
        insurance_id: memberId || null,
      }, getAuthHeader());
      setSavedProvider(data.insurance_provider || null);
      toast.success(data.insurance_provider ? `Saved — we'll flag in-network clinicians for ${data.insurance_provider}` : 'Insurance cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save insurance');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-cn-primary animate-spin" /></div>;
  }

  return (
    <div data-testid="patient-insurance-card">
      <CardHead icon={CreditCard} eyebrow="Coverage" title="Your insurance" />
      <p className="text-sm text-cn-text-muted mt-3">
        Add your plan and we'll highlight <span className="font-semibold text-cn-primary">in-network</span> clinicians as you search.
      </p>

      <div className="mt-5 space-y-4">
        <div>
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">Insurance provider</p>
          <Select value={payerId} onValueChange={setPayerId}>
            <SelectTrigger className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5" data-testid="patient-payer-select">
              <SelectValue placeholder="Select your insurer" />
            </SelectTrigger>
            <SelectContent>
              {payers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">Member ID (optional)</p>
          <Input
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            placeholder="e.g. ABC123456789"
            className="h-11 rounded-2xl border-cn-border bg-white dark:bg-white/5"
            data-testid="patient-member-id"
          />
        </div>

        {savedProvider && (
          <div className="flex items-center gap-2 p-3 rounded-2xl bg-cn-success/8 border border-cn-success/20" data-testid="patient-insurance-current">
            <BadgeCheck className="w-4 h-4 text-cn-success flex-shrink-0" />
            <p className="text-xs text-cn-text">On file: <span className="font-semibold">{savedProvider}</span></p>
          </div>
        )}
      </div>

      <Button onClick={save} disabled={saving} className="cn-btn cn-btn-primary w-full mt-5 !py-2.5" data-testid="patient-save-insurance">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        {saving ? 'Saving…' : 'Save my insurance'}
      </Button>
    </div>
  );
};
